import React, { useState, useEffect } from 'react';
import { useMutation, useQuery } from 'react-query';
import { toast } from 'react-hot-toast';
import { 
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  Building2,
  Car,
  Gift,
  Zap,
  Receipt,
  Settings
} from 'lucide-react';
import { api } from '../../../utils/api';
import Step1RegionalOffice from './steps/Step1RegionalOffice';
import Step2VehicleTypes from './steps/Step2VehicleTypes';
import Step3CouponsIncentives from './steps/Step3CouponsIncentives';
import Step4SurgeZones from './steps/Step4SurgeZones';
import Step5FeesandTaxes from './steps/Step5FeesandTaxes';
import Step6Review from './steps/Step6Review';

const CreateFareStepper = ({ editingConfig, onBack, onSave }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    regionCode: '',
    currency: 'INR',
    regionalOffice: '',
    vehicleTypes: {},
    surge: {
      enabled: true,
      multipliers: []
    },
    fees: {},
    dynamicFees: {},
    dynamicTaxes: {},
    dynamicCouponRules: [],
    incentives: {
      driverIncentives: [],
      driverPenalties: []
    },
    cancellationPolicy: {
      freeCancellationWindow: 2,
      cancellationFees: []
    },
    roundingRules: {
      enabled: true,
      strategy: 'nearest',
      decimalPlaces: 2
    },
    zones: {},
    isActive: true,
    dueRepayCycle: {
      type: 'day',
      value: '20'
    }
  });

  const steps = [
    { id: 1, name: 'Regional Office', icon: Building2, description: 'Select regional office' },
    { id: 2, name: 'Vehicle Types', icon: Car, description: 'Configure vehicle pricing' },
    { id: 3, name: 'Coupons & Incentives', icon: Gift, description: 'Set up promotions' },
    { id: 4, name: 'Surge & Zones', icon: Zap, description: 'Configure surge pricing' },
    { id: 5, name: 'Fees & Taxes', icon: Receipt, description: 'Configure fees and taxes' },
    { id: 6, name: 'Review', icon: CheckCircle, description: 'Review and save' }
  ];

  // Load editing config data
  useEffect(() => {
    if (editingConfig) {
      setFormData({
        regionCode: editingConfig.regionCode || '',
        currency: editingConfig.currency || 'INR',
        regionalOffice: editingConfig.regionalOffice?._id || '',
        vehicleTypes: editingConfig.vehicleTypes || {},
        surge: editingConfig.surge || { enabled: true, multipliers: [] },
        fees: editingConfig.fees || {},
        dynamicFees: editingConfig.dynamicFees || {},
        dynamicTaxes: editingConfig.dynamicTaxes || {},
        dynamicCouponRules: editingConfig.dynamicCouponRules || [],
        incentives: editingConfig.incentives || { driverIncentives: [], driverPenalties: [] },
        cancellationPolicy: editingConfig.cancellationPolicy || { freeCancellationWindow: 2, cancellationFees: [] },
        roundingRules: editingConfig.roundingRules || { enabled: true, strategy: 'nearest', decimalPlaces: 2 },
        zones: editingConfig.zones || {},
        isActive: editingConfig.isActive !== undefined ? editingConfig.isActive : true,
        dueRepayCycle: editingConfig.dueRepayCycle || { type: 'day', value: '20' },
        tripAcceptDuration: editingConfig.tripAcceptDuration || 30
      });
    }
  }, [editingConfig]);

  const createMutation = useMutation(
    (data) => api.post('/fare', data),
    {
      onSuccess: () => {
        toast.success('Fare configuration created successfully');
        onSave();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to create fare configuration');
      }
    }
  );

  const updateMutation = useMutation(
    (data) => api.put(`/fare/${editingConfig._id}`, data),
    {
      onSuccess: () => {
        toast.success('Fare configuration updated successfully');
        onSave();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update fare configuration');
      }
    }
  );

  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (stepId) => {
    if (stepId <= currentStep) {
      setCurrentStep(stepId);
    }
  };

  const handleFormDataChange = (newData) => {
    setFormData(prev => ({ ...prev, ...newData }));
  };

  const handleSave = () => {
    if (editingConfig) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  const isStepValid = (step) => {
    switch (step) {
      case 1:
        return formData.regionCode && formData.regionalOffice;
      case 2:
        return Object.keys(formData.vehicleTypes).length > 0;
      case 3:
        return true; // Optional step
      case 4:
        return true; // Optional step
      case 5:
        return true; // Optional step
      case 6:
        return true; // Review step
      default:
        return false;
    }
  };

  const canProceed = isStepValid(currentStep);
  const isLoading = createMutation.isLoading || updateMutation.isLoading;

  return (
    <div className="space-y-6">
      {/* Progress Steps */}
      <div className="card">
        <div className="card-body">
          <nav aria-label="Progress">
            <ol className="flex items-center justify-between">
              {steps.map((step, stepIdx) => (
                <li key={step.id} className={`flex items-center ${stepIdx !== steps.length - 1 ? 'flex-1' : ''}`}>
                  <div className="flex items-center">
                    <button
                      onClick={() => handleStepClick(step.id)}
                      disabled={step.id > currentStep}
                      className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                        step.id < currentStep
                          ? 'bg-green-500 border-green-500 text-white'
                          : step.id === currentStep
                          ? 'bg-indigo-500 border-indigo-500 text-white'
                          : 'bg-white border-gray-300 text-gray-500'
                      } ${step.id <= currentStep ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                    >
                      {step.id < currentStep ? (
                        <CheckCircle className="h-5 w-5" />
                      ) : (
                        <step.icon className="h-5 w-5" />
                      )}
                    </button>
                    <div className="ml-3">
                      <p className={`text-sm font-medium ${
                        step.id <= currentStep ? 'text-gray-900' : 'text-gray-500'
                      }`}>
                        {step.name}
                      </p>
                      <p className="text-xs text-gray-500">{step.description}</p>
                    </div>
                  </div>
                  {stepIdx !== steps.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-4 ${
                      step.id < currentStep ? 'bg-green-500' : 'bg-gray-300'
                    }`} />
                  )}
                </li>
              ))}
            </ol>
          </nav>
        </div>
      </div>

      {/* Step Content */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">
            {editingConfig ? 'Edit' : 'Create'} Fare Configuration - {steps[currentStep - 1].name}
          </h3>
        </div>
        <div className="card-body">
          {currentStep === 1 && (
            <Step1RegionalOffice
              formData={formData}
              onChange={handleFormDataChange}
            />
          )}
          {currentStep === 2 && (
            <Step2VehicleTypes
              formData={formData}
              onChange={handleFormDataChange}
            />
          )}
          {currentStep === 3 && (
            <Step3CouponsIncentives
              formData={formData}
              onChange={handleFormDataChange}
            />
          )}
          {currentStep === 4 && (
            <Step4SurgeZones
              formData={formData}
              onChange={handleFormDataChange}
            />
          )}
          {currentStep === 5 && (
             <Step5FeesandTaxes
              formData={formData}
              onChange={handleFormDataChange}
             />
          )}
          {currentStep === 6 && (
            <Step6Review
              formData={formData}
              editingConfig={editingConfig}
            />
          )}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="btn btn-outline"
        >
          <ChevronLeft className="h-4 w-4 mr-2" />
          Back to List
        </button>

        <div className="flex space-x-3">
          {currentStep > 1 && (
            <button
              onClick={handlePrevious}
              className="btn btn-outline"
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Previous
            </button>
          )}

          {currentStep < steps.length ? (
            <button
              onClick={handleNext}
              disabled={!canProceed}
              className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-2" />
            </button>
          ) : (
            <button
              onClick={handleSave}
              disabled={isLoading || !canProceed}
              className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <div className="loading-spinner mr-2"></div>
                  {editingConfig ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  {editingConfig ? 'Update Configuration' : 'Create Configuration'}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateFareStepper;
